import React, {useMemo} from 'react';

import {Skeleton} from 'src/components/skeleton';

import BoxLoading from '@pb/kit-component-box-loading';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {ContractsList} from '../../components/contracts-list';

import {AddContractDialog} from './add-contract-dialog';
import {PricingSkeleton} from './pricing-skeleton';

import type {McpContract} from 'src/openapi/admin/models/McpContract';
import type {McpUpdateContractRequest} from 'src/openapi/admin/models/McpUpdateContractRequest';
import type {McpUpdateCreditsRequest} from 'src/openapi/admin/models/McpUpdateCreditsRequest';


type Props = {
    accountId: number,
    contracts: McpContract[] | null,
    contractsLoading: boolean,
    saving: boolean,
    canEdit: boolean,
    onReloadContracts: () => void,
    onUpdateContract: (contractId: number, data: McpUpdateContractRequest) => void,
    onUpdateCredits: (contractId: number, data: McpUpdateCreditsRequest) => void,
};

export const PricingView = ({
    accountId,
    contracts,
    contractsLoading,
    saving,
    canEdit,
    onReloadContracts,
    onUpdateContract,
    onUpdateCredits,
}: Props) => {
    const activeContracts = useMemo(
        () => contracts?.filter(c => c.dateStatus !== 'PAST') ?? [],
        [contracts],
    );
    const pastContracts = useMemo(
        () => contracts?.filter(c => c.dateStatus === 'PAST') ?? [],
        [contracts],
    );

    return (
        <Skeleton shown={contractsLoading} view={<PricingSkeleton/>}>
            <BoxLoading loading={saving}>
                <div style={{padding: 24}}>
                    <Group direction="v" gap="xls" alignItems="start">
                        <Group direction="v" gap="s">
                            <Item className="pb-xs">
                                <Typography fontWeight="semi-bold" variant="heading-level-3">Active Contracts</Typography>
                            </Item>

                            {activeContracts.length > 0 ? (
                                <ContractsList
                                    data={activeContracts}
                                    canEdit={canEdit}
                                    onUpdateContract={onUpdateContract}
                                    onUpdateCredits={onUpdateCredits}
                                />
                            ) : null}

                            <Group>
                                <AddContractDialog
                                    accountId={accountId}
                                    canEdit={canEdit}
                                    saving={saving}
                                    onContractCreated={onReloadContracts}
                                />
                            </Group>
                        </Group>

                        {pastContracts.length > 0 ? (
                            <Group direction="v" gap="s" alignItems="start">
                                <Item className="pb-xs">
                                    <Typography fontWeight="semi-bold" variant="heading-level-3">Past Contracts</Typography>
                                </Item>

                                <ContractsList
                                    data={pastContracts}
                                    isPastContract={true}
                                    canEdit={canEdit}
                                    onUpdateContract={onUpdateContract}
                                    onUpdateCredits={onUpdateCredits}
                                />
                            </Group>
                        ) : null}
                    </Group>
                </div>
            </BoxLoading>
        </Skeleton>
    );
};
